import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logOut, setCredentials } from "../../store/slices/auth/authSlice";
import { Mutex } from "async-mutex";

const titanApiHeader = {
  "Content-type": "application/json",
};

let baseUrl = import.meta.env.VITE_BACKEND_BASE_URL + ":" + import.meta.env.VITE_BACKEND_PORT;

export const createRequest = (url) => ({
  url,
  headers: titanApiHeader,
});

const mutex = new Mutex();
const baseQuery = fetchBaseQuery({
  baseUrl,
  credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const token = getState().auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  await mutex.waitForUnlock();
  let result = await baseQuery(args, api, extraOptions);

  /* TODO: Validate expirationDate local and refresh before sending ? */

  if (result?.error?.originalStatus === 403) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        // send refresh token to get new access token
        const refreshResult = await baseQuery("/api/users/refresh", api, extraOptions);

        if (refreshResult?.data) {
          const user = api.getState().auth.user;
          // store the new token
          api.dispatch(setCredentials({ ...refreshResult.data, user }));
          // retry the original query with new access token
          result = await baseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logOut());
        }
      } finally {
        // release must be called once the mutex should be released again.
        release();
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock();
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export const titanApi = createApi({
  reducerPath: "titanApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "allBohrkronen",
    "Product",
    "CarryElement",
    "Tragglied",
    "CharakteristischenEinwirkungen",
    "RefCapacity",
    "DesignCombination",
    "roleValidation",
    "allProjects",
    "gwp",
  ],
  endpoints: (builder) => ({
    // #region Product
    getProducts: builder.query({
      query: () => ({
        ...createRequest(`api/products`),
        method: "GET",
      }),
      providesTags: ["Product"],
    }),

    getProductById: builder.query({
      query: (id) => ({
        ...createRequest(`api/products/${encodeURIComponent(id)}`),
        method: "GET",
      }),
      providesTags: ["Product"],
    }),

    updateProduct: builder.mutation({
      query: ({ _id, ...body }) => ({
        ...createRequest(`api/products/${encodeURIComponent(_id)}`),
        method: "PUT",
        body,
      }),
      invalidatesTags: ["Product", "CarryElement", "Tragglied", "allBohrkronen"],
    }),

    addProduct: builder.mutation({
      query: (body) => ({
        ...createRequest(`api/products`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["Product", "CarryElement", "Tragglied", "allBohrkronen"],
    }),

    deleteProduct: builder.mutation({
      query: (id) => ({
        ...createRequest(`/api/products/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["Product", "CarryElement", "Tragglied", "allBohrkronen"],
    }),
    //#endregion

    // #region Bohrkronen
    // Bohrkronen details in cm
    getBohrkronen: builder.query({
      query: () => ({
        ...createRequest(`/api/bohrkronen`),
        method: "GET",
      }),
      providesTags: ["allBohrkronen"],
    }),

    deleteBohrkrone: builder.mutation({
      query: ({ id }) => ({
        ...createRequest(`/api/bohrkronen/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["allBohrkronen"],
    }),

    addBohrkrone: builder.mutation({
      query: (body) => ({
        ...createRequest(`api/bohrkronen/`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["allBohrkronen"],
    }),

    updateBorkrone: builder.mutation({
      query: ({ id, element }) => ({
        ...createRequest(`api/bohrkronen/${id}`),
        method: "PUT",
        body: { element },
      }),
      invalidatesTags: ["allBohrkronen"],
    }),
    //#endregion

    // #region Projects
    getAllProjects: builder.query({
      query: () => ({
        ...createRequest(`/api/projects/getprojects`),
        method: "GET",
      }),
      providesTags: ["allProjects"],
    }),

    updateProject: builder.mutation({
      query: (project) => ({
        ...createRequest(`/api/projects/save/${encodeURIComponent(project?._id ?? "")}`),
        method: "PUT",
        body: { ...project },
      }),
      invalidatesTags: ["allProjects"],
    }),

    saveNewProject: builder.mutation({
      query: (project) => ({
        ...createRequest(`/api/projects/save`),
        method: "POST",
        body: { ...project },
      }),
      invalidatesTags: ["allProjects"],
    }),
    // delete Project
    deleteProject: builder.mutation({
      query: ({ id }) => ({
        ...createRequest(`/api/projects/deleteProject/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["allProjects"],
    }),
    // get by ID
    getProjectById: builder.query({
      query: ({ id }) => ({
        ...createRequest(`/api/projects/getProject/${encodeURIComponent(id)}`),
        method: "GET",
      }),
      providesTags: ["allProjects"],
    }),

    //#endregion

    // #region User actions
    // Verify Email
    confirmEmail: builder.query({
      query: (rid) => ({
        ...createRequest(`/api/users/emailverif`),
        method: "GET",
        params: { rid },
      }),
    }),

    login: builder.mutation({
      query: (credentials) => ({
        ...createRequest(`/api/users/login`),
        method: "POST",
        body: { ...credentials },
      }),
      invalidatesTags: ["roleValidation"],
    }),

    register: builder.mutation({
      query: (formData) => ({
        ...createRequest(`/api/users/register`),
        method: "POST",
        body: formData,
      }),
    }),
    // #endregion

    // #region Carrying Elements
    // RKmax + Tragglied Name
    //TODO: Check for use
    getCarryElements: builder.query({
      query: ({ fd, type }) => ({
        ...createRequest(`/api/calctypes`),
        method: "GET",
        params: { fd, type },
      }),
      providesTags: ["CarryElement"],
    }),
    getAllCarryingElements: builder.query({
      query: () => ({
        ...createRequest(`/api/carryingelements`),
        method: "GET",
      }),
      providesTags: ["CarryElement"],
    }),

    deleteCarryElement: builder.mutation({
      query: ({ id }) => ({
        ...createRequest(`/api/carryingelements/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["CarryElement"],
    }),

    updateCarryElement: builder.mutation({
      query: ({ id, element }) => ({
        ...createRequest(`/api/carryingelements/${encodeURIComponent(id)}`),
        method: "PUT",
        body: { element },
      }),
      invalidatesTags: ["CarryElement"],
    }),

    createCarryElement: builder.mutation({
      query: (body) => ({
        ...createRequest(`api/carryingelements`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["CarryElement"],
    }),
    // Tragglied Detail_2
    // Bar data
    // AASHTO Daten
    getCarryingElement: builder.query({
      query: (traggliedName) => ({
        ...createRequest(`/api/carrying_elements/${encodeURIComponent(traggliedName)}`),
        method: "GET",
      }),
      invalidatesTags: ["CarryElement"],
    }),
    // #endregion

    // #region Tragglieder
    getAllTragglieder: builder.query({
      query: () => ({
        ...createRequest(`/api/tragglieder`),
        method: "GET",
      }),
      providesTags: ["Tragglied"],
    }),

    deleteTragglied: builder.mutation({
      query: ({ id }) => ({
        ...createRequest(`/api/tragglieder/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["Tragglied"],
    }),

    //TODO: updateTragglied
    updateTragglied: builder.mutation({
      query: ({ id, element }) => ({
        ...createRequest(`/api/tragglieder/${encodeURIComponent(id)}`),
        method: "PUT",
        body: { element },
      }),
      invalidatesTags: ["Tragglied"],
    }),

    createTragglied: builder.mutation({
      query: (body) => ({
        ...createRequest(`api/tragglieder`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["Tragglied"],
    }),

    // Tragglied details
    getTraggliedDaten: builder.query({
      query: (traggliedName) => ({
        ...createRequest(`/api/tragglieder/${encodeURIComponent(traggliedName)}`),
        method: "GET",
      }),
    }),

    // #endregion

    // #region CharakteristischenEinwirkungen
    // Bemessungslast Parameter für Typ: dauerhaft / temporär
    getCharakteristischenEinwirkungen: builder.query({
      query: (typ) => ({
        ...createRequest(`/api/charakteristischenEinwirkungen/${encodeURIComponent(typ)}`),
        method: "GET",
      }),
      //invalidatesTags: ["CharakteristischenEinwirkungen"],
    }),

    getCharakteristischenEinwirkungenAll: builder.query({
      query: () => ({
        ...createRequest(`/api/values/charakteristischenEinwirkungen`),
        method: "GET",
      }),
      providesTags: ["CharakteristischenEinwirkungen"],
    }),

    createCharakteristischenEinwirkungen: builder.mutation({
      query: (body) => ({
        ...createRequest(`api/values/charakteristischenEinwirkungen`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["CharakteristischenEinwirkungen"],
    }),

    deleteCharakteristischenEinwirkungen: builder.mutation({
      query: ({ id }) => ({
        ...createRequest(`/api/values/charakteristischenEinwirkungen/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["CharakteristischenEinwirkungen"],
    }),

    updateCharakteristischenEinwirkungen: builder.mutation({
      query: ({ id, element }) => ({
        ...createRequest(`/api/values/charakteristischenEinwirkungen/${encodeURIComponent(id)}`),
        method: "PUT",
        body: { element },
      }),
      invalidatesTags: ["CharakteristischenEinwirkungen"],
    }),
    // #endregion

    // #region RefCapacity
    // refCapacity für aashto
    getRefCapacity: builder.query({
      query: (maxRef) => ({
        ...createRequest(`/api/aashto/refCapacity?maxRefCapacity=${encodeURIComponent(maxRef)}`),
        method: "GET",
      }),
    }),

    getRefCapacityAll: builder.query({
      query: () => ({
        ...createRequest(`/api/refCapacity`),
        method: "GET",
      }),
      providesTags: ["RefCapacity"],
    }),

    createRefCapacity: builder.mutation({
      query: (body) => ({
        ...createRequest(`/api/refCapacity/`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["RefCapacity"],
    }),

    deleteRefCapacity: builder.mutation({
      query: ({ id }) => ({
        ...createRequest(`/api/refCapacity/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["RefCapacity"],
    }),

    updateRefCapacity: builder.mutation({
      query: ({ id, element }) => ({
        ...createRequest(`/api/refCapacity/${encodeURIComponent(id)}`),
        method: "PUT",
        body: { element },
      }),
      invalidatesTags: ["RefCapacity"],
    }),
    // #endregion

    // #region Design Combinations
    // just for AASHTO
    getDesignCombination: builder.query({
      query: () => ({
        ...createRequest(`/api/aashto/designCombination`),
        method: "GET",
      }),
    }),

    getDesignCombinationAll: builder.query({
      query: () => ({
        ...createRequest(`/api/designCombination`),
        method: "GET",
      }),
      providesTags: ["DesignCombination"],
    }),

    createDesignCombinatoin: builder.mutation({
      query: (body) => ({
        ...createRequest(`/api/designCombination/`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["DesignCombination"],
    }),

    deleteDesignCombinatoin: builder.mutation({
      query: ({ id }) => ({
        ...createRequest(`/api/designCombination/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["DesignCombination"],
    }),

    updateDesignCombinatoin: builder.mutation({
      query: ({ id, element }) => ({
        ...createRequest(`/api/designCombination/${encodeURIComponent(id)}`),
        method: "PUT",
        body: { element },
      }),
      invalidatesTags: ["DesignCombination"],
    }),

    getAllGWPs: builder.query({
      query: () => ({
        ...createRequest(`/api/gwp`),
        method: "GET",
      }),
      providesTags: ["gwp"],
    }),

    getGWPByCarryingElementName: builder.query({
      query: (carryingElementName) => ({
        ...createRequest(
          `/api/gwp/getByCarryingElementName/${encodeURIComponent(carryingElementName)}`
        ),
        method: "GET",
      }),
      providesTags: ["gwp"],
    }),

    createGWP: builder.mutation({
      query: (body) => ({
        ...createRequest(`/api/gwp`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["gwp"],
    }),

    updateGWP: builder.mutation({
      query: ({ id, element }) => ({
        ...createRequest(`/api/gwp/${encodeURIComponent(id)}`),
        method: "PUT",
        body: { element },
      }),
      invalidatesTags: ["gwp"],
    }),

    deleteGWP: builder.mutation({
      query: ({ id }) => ({
        ...createRequest(`/api/gwp/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["gwp"],
    }),

    getAllBKDiameters: builder.query({
      query: () => ({
        ...createRequest(`/api/bkdiameter`),
        method: "GET",
      }),
      providesTags: ["bkDiameter"],
    }),

    getBKDiameterByCarryingElementName: builder.query({
      query: (carryingElementName) => ({
        ...createRequest(
          `/api/bkdiameter/getByCarryingElementName/${encodeURIComponent(carryingElementName)}`
        ),
        method: "GET",
      }),
      providesTags: ["bkDiameter"],
    }),

    createBKDiameter: builder.mutation({
      query: (body) => ({
        ...createRequest(`/api/bkdiameter`),
        method: "POST",
        body,
      }),
      invalidatesTags: ["bkDiameter"],
    }),

    updateBKDiameter: builder.mutation({
      query: ({ id, element }) => ({
        ...createRequest(`/api/bkdiameter/${encodeURIComponent(id)}`),
        method: "PUT",
        body: { element },
      }),
      invalidatesTags: ["bkDiameter"],
    }),

    deleteBKDiameter: builder.mutation({
      query: ({ id }) => ({
        ...createRequest(`/api/bkdiameter/${encodeURIComponent(id)}`),
        method: "DELETE",
      }),
      invalidatesTags: ["bkDiameter"],
    }),
    // #endregion
  }),
});

// https://redux-toolkit.js.org/rtk-query/api/created-api/hooks#usequery
// same name as above with use___Query
export const {
  // Mutation's
  useSaveNewProjectMutation,
  useUpdateProjectMutation,
  useDeleteProjectMutation,
  useLoginMutation,
  useRegisterMutation,
  useDeleteCarryElementMutation,
  useUpdateCarryElementMutation,
  useCreateCarryElementMutation,
  useDeleteTraggliedMutation,
  useCreateTraggliedMutation,
  useUpdateTraggliedMutation,
  useAddProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useUpdateBorkroneMutation,
  useAddBohrkroneMutation,
  useDeleteBohrkroneMutation,
  useCreateRefCapacityMutation,
  useDeleteRefCapacityMutation,
  useUpdateRefCapacityMutation,
  useCreateCharakteristischenEinwirkungenMutation,
  useDeleteCharakteristischenEinwirkungenMutation,
  useUpdateCharakteristischenEinwirkungenMutation,
  useCreateDesignCombinatoinMutation,
  useUpdateDesignCombinatoinMutation,
  useDeleteDesignCombinatoinMutation,

  // Queries
  useGetProductsQuery,
  useGetProductByIdQuery,
  useConfirmEmailQuery,
  useGetProjectByIdQuery,
  useGetAllProjectsQuery,
  useGetCarryElementsQuery,
  useGetAllCarryingElementsQuery,

  useGetBohrkronenQuery,
  useGetTraggliedDatenQuery,
  useGetAllTraggliederQuery,
  useGetCarryingElementQuery,
  useGetCharakteristischenEinwirkungenQuery,
  useGetCharakteristischenEinwirkungenAllQuery,
  useGetRefCapacityQuery,
  useGetRefCapacityAllQuery,
  useGetDesignCombinationQuery,
  useGetDesignCombinationAllQuery,

  useGetAllGWPsQuery,
  useGetGWPByCarryingElementNameQuery,
  useCreateGWPMutation,
  useUpdateGWPMutation,
  useDeleteGWPMutation,

  useGetAllBKDiametersQuery,
  useGetBKDiameterByCarryingElementNameQuery,
  useCreateBKDiameterMutation,
  useUpdateBKDiameterMutation,
  useDeleteBKDiameterMutation,
} = titanApi;
